import { OPTION_SEARCH_SORT_KEYS, type OptionSearchConfig, type OptionSearchSortKey } from '@einhaus/project314-types';

/**
 * Editable form representation of an OptionSearchConfig: every optional
 * numeric filter is `number | null` so empty inputs round-trip cleanly.
 */
export interface OptionSearchConfigFormState {
    optionType: 'c' | 'p';
    sortBy: OptionSearchSortKey;
    sortOrder: 'asc' | 'desc';
    strikePriceLow: number | null;
    strikePriceHigh: number | null;
    premiumLow: number | null;
    premiumHigh: number | null;
    daysToExpirationLow: number | null;
    daysToExpirationHigh: number | null;
    percentageOTMLow: number | null;
    percentageOTMHigh: number | null;
    percentageITMLow: number | null;
    percentageITMHigh: number | null;
    arorcLow: number | null;
    arorcHigh: number | null;
    putSellScoreLow: number | null;
    putSellScoreHigh: number | null;
    probabilityOTMLow: number | null;
    probabilityOTMHigh: number | null;
    maxSpreadAmount: number | null;
    bidSizeMin: number | null;
    askSizeMin: number | null;
    volumeMin: number | null;
    openInterestMin: number | null;
}

type OptionSearchNumericField = Exclude<keyof OptionSearchConfigFormState, 'optionType' | 'sortBy' | 'sortOrder'>;

export const OPTION_SEARCH_NUMERIC_FIELDS: readonly OptionSearchNumericField[] = [
    'strikePriceLow',
    'strikePriceHigh',
    'premiumLow',
    'premiumHigh',
    'daysToExpirationLow',
    'daysToExpirationHigh',
    'percentageOTMLow',
    'percentageOTMHigh',
    'percentageITMLow',
    'percentageITMHigh',
    'arorcLow',
    'arorcHigh',
    'putSellScoreLow',
    'putSellScoreHigh',
    'probabilityOTMLow',
    'probabilityOTMHigh',
    'maxSpreadAmount',
    'bidSizeMin',
    'askSizeMin',
    'volumeMin',
    'openInterestMin'
];

export const DEFAULT_OPTION_SEARCH_FORM_STATE: OptionSearchConfigFormState = {
    optionType: 'p',
    sortBy: 'premiumArorc',
    sortOrder: 'desc',
    strikePriceLow: null,
    strikePriceHigh: null,
    premiumLow: null,
    premiumHigh: null,
    daysToExpirationLow: null,
    daysToExpirationHigh: null,
    percentageOTMLow: null,
    percentageOTMHigh: null,
    percentageITMLow: null,
    percentageITMHigh: null,
    arorcLow: null,
    arorcHigh: null,
    putSellScoreLow: null,
    putSellScoreHigh: null,
    probabilityOTMLow: null,
    probabilityOTMHigh: null,
    maxSpreadAmount: null,
    bidSizeMin: null,
    askSizeMin: null,
    volumeMin: null,
    openInterestMin: null
};

/**
 * Legacy sortBy spellings the Rust sorter also accepts, canonicalized on load
 * so stored configs written before the sort-key enum keep their meaning.
 */
const SORT_KEY_ALIASES: Record<string, OptionSearchSortKey> = {
    arorc: 'premiumArorc',
    probabilityOTM: 'probabilityOtm',
    midIv: 'impliedVolatility',
    bid: 'bidPrice',
    ask: 'askPrice',
    daysToExpiry: 'daysToExpiration'
};

export const normalizeOptionSearchSortKey = (sortBy: string): OptionSearchSortKey => {
    if ((OPTION_SEARCH_SORT_KEYS as readonly string[]).includes(sortBy)) return sortBy as OptionSearchSortKey;

    return SORT_KEY_ALIASES[sortBy] ?? 'premiumArorc';
};

export const optionSearchConfigToFormState = (config: OptionSearchConfig | null | undefined): OptionSearchConfigFormState => {
    if (!config) return { ...DEFAULT_OPTION_SEARCH_FORM_STATE };

    const state: OptionSearchConfigFormState = {
        ...DEFAULT_OPTION_SEARCH_FORM_STATE,
        optionType: config.optionType,
        sortBy: normalizeOptionSearchSortKey(config.sortBy),
        sortOrder: config.sortOrder
    };

    for (const field of OPTION_SEARCH_NUMERIC_FIELDS) {
        state[field] = config[field] ?? null;
    }

    return state;
};

/**
 * Builds the config sent to the backend. Empty fields are omitted entirely
 * (the finder treats a missing key as "filter inactive"), and the legacy
 * runtime-only keys (`underlyingPrice`, `mode`) are never emitted.
 */
export const formStateToOptionSearchConfig = (state: OptionSearchConfigFormState): OptionSearchConfig => {
    const config: OptionSearchConfig = {
        optionType: state.optionType,
        sortBy: state.sortBy,
        sortOrder: state.sortOrder
    };

    for (const field of OPTION_SEARCH_NUMERIC_FIELDS) {
        const value = state[field];

        if (value !== null) config[field] = value;
    }

    return config;
};

const LOW_HIGH_PAIRS: readonly (readonly [OptionSearchNumericField, OptionSearchNumericField])[] = [
    ['strikePriceLow', 'strikePriceHigh'],
    ['premiumLow', 'premiumHigh'],
    ['daysToExpirationLow', 'daysToExpirationHigh'],
    ['percentageOTMLow', 'percentageOTMHigh'],
    ['percentageITMLow', 'percentageITMHigh'],
    ['arorcLow', 'arorcHigh'],
    ['putSellScoreLow', 'putSellScoreHigh'],
    ['probabilityOTMLow', 'probabilityOTMHigh']
];

/** Returns per-field error messages, keyed by form-state field name. */
export const validateOptionSearchFormState = (state: OptionSearchConfigFormState): Record<string, string> => {
    const errors: Record<string, string> = {};

    for (const [lowField, highField] of LOW_HIGH_PAIRS) {
        const low = state[lowField];
        const high = state[highField];

        if (low !== null && high !== null && low > high) {
            errors[lowField] = 'Low must be ≤ high';
        }
    }

    return errors;
};

/** Chain-window defaults the option finder applies when the strategy-level DTE bounds are empty. */
export const DEFAULT_CHAIN_MIN_DTE = 5;
export const DEFAULT_CHAIN_MAX_DTE = 30;

export interface OptionSearchConfigAdvisory {
    severity: 'warning' | 'info';
    message: string;
}

interface OptionSearchStrategyContext {
    strategyMinDte: number | null;
    strategyMaxDte: number | null;
    strategyMaxSpreadPercentage: number | null;
}

/**
 * Cross-checks the search config against the strategy-level fields the option
 * finder also applies in search mode: the chain-fetch DTE window (defaulting
 * to 5–30 when the strategy bounds are empty) and the percentage spread guard
 * that runs on the final pick in addition to the config's dollar spread
 * filter. These interactions are otherwise invisible in the UI.
 */
export const getOptionSearchConfigAdvisories = (
    state: OptionSearchConfigFormState,
    context: OptionSearchStrategyContext
): OptionSearchConfigAdvisory[] => {
    const advisories: OptionSearchConfigAdvisory[] = [];

    const chainMin = context.strategyMinDte ?? DEFAULT_CHAIN_MIN_DTE;
    const chainMax = context.strategyMaxDte ?? DEFAULT_CHAIN_MAX_DTE;
    const chainMinLabel = context.strategyMinDte === null ? `${chainMin} — the default while Min DTE is empty` : `${chainMin}`;
    const chainMaxLabel = context.strategyMaxDte === null ? `${chainMax} — the default while Max DTE is empty` : `${chainMax}`;

    if (state.daysToExpirationLow !== null && state.daysToExpirationLow > chainMax) {
        advisories.push({
            severity: 'warning',
            message: `No option can ever match: DTE Low (${state.daysToExpirationLow}) is above the chain window maximum (${chainMaxLabel}). Options are only fetched between ${chainMin} and ${chainMax} days to expiration.`
        });
    } else if (state.daysToExpirationHigh !== null && state.daysToExpirationHigh < chainMin) {
        advisories.push({
            severity: 'warning',
            message: `No option can ever match: DTE High (${state.daysToExpirationHigh}) is below the chain window minimum (${chainMinLabel}). Options are only fetched between ${chainMin} and ${chainMax} days to expiration.`
        });
    } else {
        if (state.daysToExpirationLow !== null && state.daysToExpirationLow < chainMin) {
            advisories.push({
                severity: 'warning',
                message: `DTE Low (${state.daysToExpirationLow}) is below the chain window minimum (${chainMinLabel}); options under ${chainMin} days to expiration are never fetched.`
            });
        }

        if (state.daysToExpirationHigh !== null && state.daysToExpirationHigh > chainMax) {
            advisories.push({
                severity: 'warning',
                message: `DTE High (${state.daysToExpirationHigh}) is above the chain window maximum (${chainMaxLabel}); options beyond ${chainMax} days to expiration are never fetched.`
            });
        }
    }

    if (state.maxSpreadAmount !== null && context.strategyMaxSpreadPercentage !== null) {
        advisories.push({
            severity: 'info',
            message: `Both spread limits apply: candidates are filtered by Max Spread $${state.maxSpreadAmount} during the search, and the final selection must also pass the strategy's ${context.strategyMaxSpreadPercentage}% Max Spread guard.`
        });
    }

    return advisories;
};
